import React, { useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import CampaignItem from './CampaignItem'
import Button from '../../../../components/Button'
import ProgressButton from '../../../../components/Form/ProgressButton'
import HistoryItem from './HistoryItem'
import ItemsLoading from '../../../../components/Loaders/ItemsLoading'
import SkeletonHistoryItem from './HistoryItem/Skeleton'

import useManageDrawer from '../../../../hooks/formHooks/useManageDrawer'

import { createJsonFromQueryString } from '../../../../helpers/url'

import {
  applyRightSuggestedChangesErrorSelector,
  applyRightSuggestedChangesIsLoadingSelector,
  applyRightSuggestedChangesWasCreatedSelector,
  partnerbrainHistoryErrorSelector,
  partnerbrainHistoryIsLoadingSelector,
  partnerbrainHistoryNextSelector,
  partnerbrainHistorySelector,
  partnerbrainHistoryWasLoadedSelector,
  rightSuggestedChangesSelector
} from '../../../../modules/selectors/rights'
import {
  applyRightSuggestedChanges,
  clearRightSuggestedChanges,
  clearApplyRightSuggestedChanges,
  clearPartnerbrainHistory,
  getPartnerbrainHistory
} from '../../../../modules/actions/rights'

import useDrawerFormStyles from '../../../../styles/common/drawerForms'
import useStyles from './styles'

const PartnerbrainOptimisationsForm = ({ rightId }) => {
  const drawerFormClasses = useDrawerFormStyles()
  const classes = useStyles()

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const rightSuggestedChanges = useSelector(rightSuggestedChangesSelector)
  const partnerbrainHistory = useSelector(partnerbrainHistorySelector)
  const partnerbrainHistoryNext = useSelector(partnerbrainHistoryNextSelector)
  const applyChangesIsLoading = useSelector(applyRightSuggestedChangesIsLoadingSelector)
  const applyChangesError = useSelector(applyRightSuggestedChangesErrorSelector)
  const applyChangesWasApplied = useSelector(applyRightSuggestedChangesWasCreatedSelector)

  const { handleFormClose } = useManageDrawer({})

  const { detail: noSuggestedChangesMessage, roas = [], lead = [], cpc = [], cpe = [] } = rightSuggestedChanges

  const isPartnerbrainHistoryEmpty = !partnerbrainHistory.length

  const gatheredCampaigns = useMemo(() => [...roas, ...lead, ...cpc, ...cpe], [cpc, cpe, lead, roas])

  const applyRightChangesHandler = () => {
    dispatch(applyRightSuggestedChanges(rightId))
  }

  const applyRightChangesClearHandler = () => {
    dispatch(clearApplyRightSuggestedChanges())
  }

  const loadMorePartnerbrainHistoryHandler = () => {
    const nextQueryParams =
      partnerbrainHistoryNext && createJsonFromQueryString(`?${partnerbrainHistoryNext.split('?')[1]}`)

    dispatch(getPartnerbrainHistory(nextQueryParams))
  }

  const onSuccessChangesWasApplied = () => {
    handleFormClose()

    dispatch(clearRightSuggestedChanges())
    dispatch(clearPartnerbrainHistory())
  }

  return (
    <>
      <div className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>{t('Suggested Updates')}</h3>
        {noSuggestedChangesMessage ? (
          <p>{t(noSuggestedChangesMessage)}</p>
        ) : (
          gatheredCampaigns.map((campaign, index) => <CampaignItem key={index} campaign={campaign} />)
        )}
      </div>
      {!noSuggestedChangesMessage && (
        <div className={classes.actionButtons}>
          <Button onClick={handleFormClose}>{t('Cancel')}</Button>
          <ProgressButton
            isFormLoading={applyChangesIsLoading}
            formError={applyChangesError}
            successSubmit={applyChangesWasApplied}
            onClick={applyRightChangesHandler}
            clearHandler={applyRightChangesClearHandler}
            onSuccessSubmit={onSuccessChangesWasApplied}
          >
            {t('Apply Changes')}
          </ProgressButton>
        </div>
      )}
      <div className={drawerFormClasses.section}>
        <h3 className={drawerFormClasses.sectionTitle}>{t('History')}</h3>
        {isPartnerbrainHistoryEmpty ? (
          <p>{t('There are no history items to show.')}</p>
        ) : (
          <ItemsLoading
            SkeletonComponent={SkeletonHistoryItem}
            itemsLength={partnerbrainHistory.length}
            errorSelector={partnerbrainHistoryErrorSelector}
            isLoadingSelector={partnerbrainHistoryIsLoadingSelector}
            wasLoadedSelector={partnerbrainHistoryWasLoadedSelector}
            loadMore={partnerbrainHistoryNext ? loadMorePartnerbrainHistoryHandler : null}
          >
            {partnerbrainHistory.map((item, index) => (
              <HistoryItem key={index} item={item} />
            ))}
          </ItemsLoading>
        )}
      </div>
    </>
  )
}

export default PartnerbrainOptimisationsForm
